/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [friends.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 03.03.20, 16:15
 */
import React, {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {useAuthorization, useFriendData} from "../../selectors";
import {
    asyncAcceptFriend,
    asyncAddFriend,
    asyncBlockFriend,
    asyncCancelFriend,
    asyncDeleteFriend,
    asyncIgnoreFriend,
    asyncUnblockFriend,
    localOpenFriendChat,
    localResetChatEntries,
    localResetFriends,
    localResetPending
} from "../../actions";
import FriendEntry from "./friend-entry";
import PendingRequestEntry from "./pending-request-entry";
import PendingAskedEntry from "./pending-asked-entry";
import {useIsMobile, useLanguage} from "../../components/configuration/configuration";
import {notify, notifyWarning} from "../../components/notifications/notify";
import {cn} from "../../components/util/text-utils";
import SmoothScrollbar from "../../components/scrollbars/smooth-scrollbar";


const Friends = ({className, username}) => {
    const {isAuthorized, authorizedUsername} = useAuthorization();
    const {friends, pending} = useFriendData();
    const currentChat = useRef(null);
    const isSelf = useRef(username === authorizedUsername);
    const Lang = useLanguage();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();

    useEffect(() => {
        return () => {
            // explicitely not resetting current open chat here - see parent component FriendsSpace
            dispatch(localResetChatEntries());
            dispatch(localResetFriends());
            dispatch(localResetPending());
        };
    }, [])

    const isCurrentChat = friend => {
        return currentChat.current && currentChat.current.id === friend.id;
    }

    const handleOnBlock = friend => event => {
        event.preventDefault();
        dispatch(asyncBlockFriend(authorizedUsername, friend.username, (params) => {
            notify(Lang.chat.info.blocked(friend.firstname));
            isCurrentChat(friend) && dispatch(localOpenFriendChat(null));
        }));
    }

    const handleOnUnblock = friend => event => {
        event.preventDefault();
        dispatch(asyncUnblockFriend(authorizedUsername, friend.username, (params) => {
            notify(Lang.chat.info.unblocked(friend.firstname));
        }));
    }

    const handleOnDelete = friend => event => {
        event.preventDefault();
        dispatch(asyncDeleteFriend(authorizedUsername, friend.username, (params) => {
            notifyWarning(Lang.chat.info.deleted(friend.firstname));
            isCurrentChat(friend) && dispatch(localOpenFriendChat(null));
        }));
    }

    const handleOnDenyRequest = friend => event => {
        event.preventDefault();
        dispatch(asyncIgnoreFriend(authorizedUsername, friend.username, friend => {
                notifyWarning(Lang.chat.info.deny(friend.friend.firstname));
            })
        );
    }

    const handleOnAcceptRequest = friend => event => {
        event.preventDefault();
        dispatch(asyncAcceptFriend(authorizedUsername, friend.username, friend => {
                notify(Lang.chat.info.accepted(friend.friend.firstname));
            })
        );
    }

    const handleOnCancelRequest = friend => event => {
        event.preventDefault();
        dispatch(asyncCancelFriend(authorizedUsername, friend.username, friend => {
                notifyWarning(Lang.chat.info.cancelled(friend.friend.firstname));
            })
        );
    }

    const handleOnAddFriend = friend => event => {
        event.preventDefault();
        dispatch(asyncAddFriend(authorizedUsername, friend.username, friend => {
                notifyWarning(Lang.chat.info.requested(friend.friend.firstname));
            })
        );
    }

    const handleOnOpenChat = entry => event => {
        const isSame = currentChat.current && currentChat.current.id === entry.friend.id;
        currentChat.current = isSame ? null : entry.friend;
        dispatch(localOpenFriendChat(isSame ? null : entry));
    }

    const renderFriendEntries = () => {
        return friends.map(entry => {
            const {friend, chat} = entry;
            // delivered = incoming messages and not read yet
            return <FriendEntry
                key={friend.id}
                chatEntry={entry}
                enableChat={isSelf.current}
                onBlock={handleOnBlock(friend)}
                onUnblock={handleOnUnblock(friend)}
                onDelete={handleOnDelete(friend)}
                onChat={handleOnOpenChat(entry)}
                onAddFriend={handleOnAddFriend(friend)}
            />
        })
    };

    const renderPendingRequested = () => {
        const pendingRequests = pending
            .filter(entry => entry.action === 'REQUESTED' && entry.user.username === authorizedUsername)
            .map(entry => {
                const {friend, chat} = entry;
                return <PendingRequestEntry
                    key={friend.id}
                    chatEntry={entry}
                    onAcceptRequest={handleOnAcceptRequest(friend)}
                    onDenyRequest={handleOnDenyRequest(friend)}/>
            })
        if (pendingRequests.length === 0) return null;
        return <div className='mt-4'><strong>{Lang.chat.title.pendingRequested}</strong>
            <div>{pendingRequests}</div>
        </div>;
    }

    const renderPendingAsked = () => {
        const pendingRequests = pending
            .filter(entry => entry.action === 'REQUESTING' && entry.user.username === authorizedUsername)
            .map(entry => {
                const {friend, chat} = entry;
                return <PendingAskedEntry
                    key={friend.id}
                    chatEntry={entry}
                    onCancelRequest={handleOnCancelRequest(friend)}/>
            })
        if (pendingRequests.length === 0) return null;
        return <div className='mt-4'><strong>{Lang.chat.title.pendingAsked}</strong>
            <div>{pendingRequests}</div>
        </div>;
    }

    // prevent unnecessary render from previous friends
    if (!isAuthorized || (friends.length === 0 && pending.length === 0)) return null;

    return <div className={cn('friends-container', [className])}>

        {!isMobile && (friends.length > 0) &&
            <SmoothScrollbar className="friends-scrollbar-container mt-1 mr--2">
                <div>{renderFriendEntries()}</div>
            </SmoothScrollbar>}

        {isMobile && (friends.length > 0) && renderFriendEntries()}

        {pending && renderPendingRequested()}
        {pending && renderPendingAsked()}
    </div>
};

export default Friends;